import { useState } from 'react'

import { Box, Form } from '@factorialco/deprecated-design-system'
import { RichTextEditor } from '@factorialco/f0-react/dist/experimental'
import {
  AtsFeedbackDetailFragment,
  AtsFeedbackEditDocument,
  AtsFeedbackEditMutation,
  AtsFileFragment,
  AtsJobApplicationNotesDocument,
} from 'generated/resources/graphql'

import UserAvatar from 'modules/core/components/UserAvatar'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { RatingSelector } from 'modules/ats/components/Feedbacks/RatingSelector'
import { VisibilityToggler } from 'modules/ats/components/Feedbacks/VisibilityToggler'
import { AtsFeedbackFormValues, toVisibilityEnum } from 'modules/ats/lib/utils/feedbacks'
import { useRichTextEditorNotesMentions } from 'modules/ats/lib/utils/useNotesMentions'

interface Props {
  feedback: AtsFeedbackDetailFragment
  onEditFinish: () => void
  enabledScoring?: boolean
  restrictedCommentsAvailable?: boolean
}

export const FeedbackEdit = ({
  feedback,
  onEditFinish,
  enabledScoring = true,
  restrictedCommentsAvailable,
}: Props) => {
  const { editorConfig } = useRichTextEditor()
  const [updateFeedback] = useMutation(AtsFeedbackEditDocument, {
    extractorFn: (data: AtsFeedbackEditMutation) => data.atsMutations.updateAtsFeedback,
    refetchQueries: [AtsJobApplicationNotesDocument],
  })
  const [accessesIds, setAccessesIds] = useState<string[] | null>([])
  const { candidate, applicationPhase, atsFiles, application, description, rating, visibility } =
    feedback

  const {
    accesses: uniqueRichTextEditorMentioningAccesses,
    loading: richTextEditorMentionsLoading,
  } = useRichTextEditorNotesMentions({
    atsJobPostingId: application?.jobPosting.id,
    candidateId: candidate.id,
  })

  const toFile = (attachments: AtsFileFragment[]): File[] => {
    return attachments.map(
      (attachment) =>
        new File([], attachment.file.filename, {
          type: attachment.file.contentType || '',
          lastModified: new Date().getTime(),
        })
    )
  }

  const form = useForm<AtsFeedbackFormValues>({
    defaultValues: {
      description,
      rating,
      atsCandidateId: candidate.id,
      atsApplicationId: application?.id,
      atsApplicationPhaseId: applicationPhase?.id,
      mentionIds: accessesIds,
      attachments: atsFiles.length ? toFile(atsFiles) : [],
      visibility: toVisibilityEnum(visibility),
    },
    async onSubmit(data, { setErrors }) {
      const result = await updateFeedback({
        variables: {
          id: feedback.id,
          ...data,
          attachments: data.attachments && data.attachments.length > 0 ? data.attachments : null,
        },
      })

      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }
      onEditFinish()

      return handled
    },
  })

  return (
    <Form padding={null}>
      <Box flexDirection="row" gap="s32" justifyContent="spaceBetween" alignItems="center">
        <Box gap="s8" flexDirection="row" alignItems="center" maxWidth="s600">
          <UserAvatar user={feedback.access ? feedback.access : undefined} size="s40" />
          {enabledScoring && (
            <RatingSelector
              value={Number(form.values.rating)}
              onChange={(rat) => form.setFieldValue('rating', rat)}
            />
          )}
        </Box>
        {restrictedCommentsAvailable && (
          <VisibilityToggler
            value={form.values.visibility}
            onChange={(value) => form.setFieldValue('visibility', value)}
          />
        )}
      </Box>

      {richTextEditorMentionsLoading ? (
        <RichTextEditor.Skeleton />
      ) : (
        <RichTextEditor
          {...editorConfig}
          title={i18n.t('ats.feedbacks.title_edit')}
          onChange={(data) => {
            form.setFieldValue('description', data?.value)
            setAccessesIds(data?.mentionIds?.map(String) ?? null)
          }}
          placeholder={i18n.t('ats.feedbacks.placeholder')}
          initialEditorState={{
            content: form.values.description || undefined,
          }}
          mentionsConfig={{
            users: uniqueRichTextEditorMentioningAccesses,
          }}
          primaryAction={{
            action: {
              label: i18n.t('common.save'),
              onClick: () => form.handleSubmit(),
              variant: 'default',
            },
          }}
          secondaryAction={{
            label: i18n.t('common.cancel'),
            onClick: onEditFinish,
            variant: 'outline',
          }}
        />
      )}
    </Form>
  )
}
